// =====================================================
// EVENT PARTICIPANTS REPOSITORY
// Stage 2: Participants & Pricing
// Repository pattern for event participants operations
// =====================================================

import * as eventParticipantsService from '@/Services/eventParticipants.service';
import type {
  ParticipantWithDetails,
  CreateParticipantData,
  ParticipantFilters,
} from '@/Services/eventParticipants.service';
import type { ParticipantType } from '@/types/pricing.types';

// ===================
// ERROR WRAPPER
// ===================

async function withErrorHandling<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    console.error('Event Participants Repository Error:', error);
    throw error;
  }
}

// ===================
// REPOSITORY FUNCTIONS
// ===================

export const eventParticipantsRepository = {
  /**
   * Get all participants for an event
   */
  async getEventParticipants(
    eventId: string,
    filters?: ParticipantFilters
  ): Promise<ParticipantWithDetails[]> {
    return withErrorHandling(() =>
      eventParticipantsService.getEventParticipants(eventId, filters)
    );
  },

  /**
   * Get a single participant
   */
  async getParticipantById(id: string): Promise<ParticipantWithDetails | null> {
    return withErrorHandling(() => eventParticipantsService.getParticipantById(id));
  },

  /**
   * Check if minister/guest is already registered
   */
  async isAlreadyRegistered(
    eventId: string,
    ministerId?: string,
    guestId?: string
  ): Promise<boolean> {
    return withErrorHandling(() =>
      eventParticipantsService.isAlreadyRegistered(eventId, ministerId, guestId)
    );
  },

  /**
   * Register a participant
   */
  async registerParticipant(data: CreateParticipantData): Promise<ParticipantWithDetails> {
    return withErrorHandling(() => eventParticipantsService.registerParticipant(data));
  },

  /**
   * Unregister a participant
   */
  async unregisterParticipant(id: string): Promise<void> {
    return withErrorHandling(() => eventParticipantsService.unregisterParticipant(id));
  },

  /**
   * Update participant data
   */
  async updateParticipant(
    id: string,
    data: Partial<CreateParticipantData>
  ): Promise<ParticipantWithDetails> {
    return withErrorHandling(() => eventParticipantsService.updateParticipant(id, data));
  },

  /**
   * Calculate price for a participant
   */
  async calculateParticipantPrice(
    eventId: string,
    basePrice: number,
    ministerId?: string,
    guestId?: string,
    registrationDate?: Date,
    eventStartDate?: Date,
    options?: { participantType?: ParticipantType }
  ) {
    return withErrorHandling(() =>
      eventParticipantsService.calculateParticipantPrice(
        eventId,
        basePrice,
        ministerId,
        guestId,
        registrationDate,
        eventStartDate,
        options
      )
    );
  },

  /**
   * Recalculate price for existing participant
   */
  async recalculateParticipantPrice(participantId: string): Promise<void> {
    return withErrorHandling(() =>
      eventParticipantsService.recalculateParticipantPrice(participantId)
    );
  },

  /**
   * Recalculate all participant prices for an event
   */
  async recalculateParticipantPrices(eventId: string, participantIds?: string[]) {
    return withErrorHandling(() =>
      eventParticipantsService.recalculateParticipantPrices(eventId, participantIds)
    );
  },

  /** Active ministers by ADB code (uppercased) — one query per 200 codes */
  async findMinistersByCodes(codes: string[]): Promise<Map<string, { id: string; full_name: string | null }>> {
    return withErrorHandling(async () => {
      const { supabase } = await import('@/supabase.config');
      const unique = [...new Set(codes.map((c) => c.trim().toUpperCase()).filter(Boolean))];
      const map = new Map<string, { id: string; full_name: string | null }>();
      for (let i = 0; i < unique.length; i += 200) {
        const { data, error } = await supabase
          .from('ministers')
          .select('id, full_name, adb_code')
          .in('adb_code', unique.slice(i, i + 200))
          .eq('is_active', true);
        if (error) throw new Error(error.message);
        (data ?? []).forEach((m) => map.set(m.adb_code.toUpperCase(), { id: m.id, full_name: m.full_name }));
      }
      return map;
    });
  },

  async recalculateAllParticipantPrices(eventId: string): Promise<void> {
    return withErrorHandling(() =>
      eventParticipantsService.recalculateAllParticipantPrices(eventId)
    );
  },

  /**
   * Get participant statistics
   */
  async getEventParticipantStatistics(eventId: string) {
    return withErrorHandling(() =>
      eventParticipantsService.getEventParticipantStatistics(eventId)
    );
  },

  /**
   * Get all active authorities (districts + regions)
   */
  async getAllActiveAuthorities(scope: 'ALL' | 'DISTRICT' | 'REGION' = 'ALL') {
    return withErrorHandling(() =>
      eventParticipantsService.getAllActiveAuthorities(scope)
    );
  },

  /**
   * Bulk register authorities as mandatory participants
   */
  async bulkRegisterAuthorities(
    eventId: string,
    scope: 'ALL' | 'DISTRICT' | 'REGION' = 'ALL',
    onProgress?: (current: number, total: number) => void
  ) {
    return withErrorHandling(() =>
      eventParticipantsService.bulkRegisterAuthorities(eventId, scope, onProgress)
    );
  },

  /**
   * Update participant payment status
   */
  async updateParticipantPaymentStatus(
    id: string,
    paymentStatus: 'PAID' | 'PENDING',
    paymentAmount?: number
  ) {
    return withErrorHandling(() =>
      eventParticipantsService.updateParticipantPaymentStatus(id, paymentStatus, paymentAmount)
    );
  },

  /**
   * Bulk update payment status for multiple participants
   */
  async bulkUpdatePaymentStatus(participantIds: string[], paymentStatus: 'PAID' | 'PENDING') {
    return withErrorHandling(() =>
      eventParticipantsService.bulkUpdatePaymentStatus(participantIds, paymentStatus)
    );
  },

  /**
   * Find minister by ADB code
   */
  async findMinisterByCode(code: string): Promise<{ id: string; full_name: string } | null> {
    return withErrorHandling(async () => {
      const { supabase } = await import('@/supabase.config');
      const { data, error } = await supabase
        .from('ministers')
        .select('id, full_name')
        .eq('adb_code', code.toUpperCase())
        .eq('is_active', true)
        .single();

      if (error || !data || !data.full_name) {
        return null;
      }

      return data as { id: string; full_name: string };
    });
  },
};
